import React from "react";
import { useTranslation } from "react-i18next";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as CalendarSVG } from "go-core/images/svg/calendar.svg";
import FormatResourceStatus from "../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import Tag from "../../../../../../components/Common/Tag/Tag";
import { ReactComponent as CalendarDangerSVG } from "../../../../../../images/svg/subscriptions/calendar-danger.svg";
import { SubscriptionApi } from "../../../../../../services/Api/Subscription/subscriptionTypes";
import { RenderApplicationLogo } from "../../../RenderApplicationLogo/RenderApplicationLogo";

interface Props {
	subscription: SubscriptionApi;
}

const OrganizationSingleSubscriptionItem = ({ subscription }: Props) => {
	const isSubscriptionInactive = subscription.status === "CANCELLED" || subscription.status === "NON_RENEWING";
	const { t } = useTranslation("admin");

	const splitDateWithComma = (date: string) => {
		return date?.split(",")[0];
	};

	const drawDate = () => {
		if (
			subscription.status === "CANCELLED" &&
			(subscription.cancellation_reason === "MISSING_CUSTOMER_DATA" ||
				subscription.cancellation_reason === "WAITING_PAYMENT") &&
			subscription.cancelled_at
		)
			return (
				<>
					<CalendarDangerSVG />
					<span className="label text-danger">
						{t("modules.organization.field.subscription_expired.title")}{" "}
						{splitDateWithComma(FormatDate(subscription.cancelled_at))}
					</span>
				</>
			);
		if (subscription.cancelled_at && subscription.status === "CANCELLED" && subscription.cancellation_reason)
			return (
				<>
					<CalendarSVG />
					<span className="label">
						{t("modules.organization.field.cancellation.title")}{" "}
						{splitDateWithComma(FormatDate(subscription.cancelled_at))}
					</span>
				</>
			);
		if (subscription.current_term_end && subscription.status === "ACTIVE")
			return (
				<>
					<CalendarSVG />
					<span className="label">
						{t("lib:common.word.to")} {splitDateWithComma(FormatDate(subscription.current_term_end))}
					</span>
				</>
			);
		if (subscription.trial_end && subscription.status === "IN_TRIAL")
			return (
				<>
					<CalendarSVG />
					<span className="label">
						{t("lib:common.word.to")} {splitDateWithComma(FormatDate(subscription.trial_end))}
					</span>
				</>
			);
		if (subscription.cancelled_at && subscription.status === "NON_RENEWING")
			return (
				<>
					<CalendarSVG />
					<span className="label">
						{t("lib:common.word.from")} {splitDateWithComma(FormatDate(subscription.cancelled_at))}
					</span>
				</>
			);
		if (subscription.pause_date && subscription.resume_date && subscription.status === "PAUSED")
			return (
				<>
					<CalendarSVG />
					<span className="label">
						{t("lib:common.word.from")} {splitDateWithComma(FormatDate(subscription.pause_date))}{" "}
						{t("lib:common.word.to")} {splitDateWithComma(FormatDate(subscription.resume_date))}
					</span>
				</>
			);
		return null;
	};

	const getMonthlyAddonsTotalPrice = () => {
		let amount: number = 0;
		if (subscription.billing_period === "YEARLY") {
			if (subscription.monthly_sub_item && subscription.monthly_sub_item.addons.length > 0) {
				amount = Number(
					subscription.monthly_sub_item.addons
						.map((addon) => addon.total_price.amount || 0)
						.reduce((acc, cur) => acc + cur, 0)
				);
			}
		}
		return { amount, currency: subscription.total_money.currency };
	};

	const drawTotalMoneyAmount = () => {
		const billingPeriod = subscription.billing_period === "YEARLY" ? "rok" : "mies.";
		return (
			<>
				<h6>
					{FormatMoney(subscription.total_money)}/{billingPeriod}
				</h6>
				{getMonthlyAddonsTotalPrice().amount > 0 && (
					<span className="text-muted">+ {FormatMoney(getMonthlyAddonsTotalPrice())}</span>
				)}
			</>
		);
	};

	const getSubscriptionExternalId = () => {
		const ids: string[] = [`#${subscription.external_id}`];
		if (subscription.monthly_sub_item) {
			ids.push(`#${subscription.monthly_sub_item.external_id}`);
		}
		return ids.join(", ");
	};

	return (
		<tr className={`${isSubscriptionInactive ? "inactive-subscription" : ""}`}>
			<td>
				<div className="d-flex flex-column">
					<div className="d-flex subscription-logo">
						<RenderApplicationLogo slug={subscription.family_id} />
						<h5 className="ms-2 mb-3">{subscription.name}</h5>
					</div>
					<div className="d-flex">
						<FormatResourceStatus status={subscription.status} />
					</div>
					<div className="subscription-date">{drawDate()}</div>
					<small className="text-muted">{getSubscriptionExternalId()}</small>
				</div>
			</td>
			<td>
				<div className="subscription-addons">
					{subscription.monthly_sub_item && (
						<div className="addon-type-label">
							<span className="label text-muted">{t("modules.organization.field.annual.title")}</span>
						</div>
					)}
					<div className="d-flex flex-wrap">
						{subscription.addons.length > 0 ? (
							subscription.addons?.map((addon, index) => {
								return (
									<Tag key={index}>
										{addon.name}
										{(addon.quantity || addon.quantity === 0) && `: ${addon.quantity}`}
									</Tag>
								);
							})
						) : (
							<Tag styles={{ color: "#6C757D" }}>{t("modules.organization.field.no_addons.title")}</Tag>
						)}
					</div>
					{subscription.monthly_sub_item && (
						<div className="addon-type-label">
							<span className="label text-muted">{t("modules.organization.field.monthly.title")}</span>
						</div>
					)}
					<div className="d-flex flex-wrap subscription-total-price">
						{subscription.monthly_sub_item?.addons.map((addon, index) => {
							return (
								<Tag key={index}>
									{addon.name}
									{addon.quantity && `: ${addon.quantity}`}
								</Tag>
							);
						})}
					</div>
				</div>
			</td>
			<td>
				<div>{drawTotalMoneyAmount()}</div>
			</td>
		</tr>
	);
};

export default OrganizationSingleSubscriptionItem;
